import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RT,
  ResponsiveContainer, LineChart, Line, Cell, PieChart, Pie, Legend
} from "recharts";
import { useAppearance } from "../../contexts/AppearanceContext";
import { Download, MessageCircle, Send, Inbox, Clock, Users, TrendingUp, CheckCheck, Zap, Phone } from "lucide-react";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_LABELS = { text: "Texto", image: "Imagem", audio: "Áudio", document: "Documento", video: "Vídeo" };
const STATUS_LABELS = { sent: "Enviado", delivered: "Entregue", read: "Lido", received: "Recebido" };
const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

function KpiCard({ icon: Icon, title, value, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider leading-tight">{title}</p>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-sm font-semibold text-slate-700 mb-3">{children}</h3>;
}

export default function WhatsAppReport({ filterHook }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("visao_geral");
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#10b981";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterHook.buildApiFilters();
      const res = await axios.post(`${API}/reports/whatsapp`, {
        period: filters.period,
        responsavel_ids: filters.responsavel_ids,
      }, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterHook]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    // KPIs
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
      "Total Conversas": data.kpis.total_conversas,
      "Msgs Enviadas": data.kpis.enviadas,
      "Msgs Recebidas": data.kpis.recebidas,
      "Taxa de Resposta (%)": data.kpis.taxa_resposta,
      "Tempo Médio Resposta": data.kpis.avg_response_fmt,
      "Leads via WhatsApp": data.kpis.wa_leads,
      "Conversão Leads (%)": data.kpis.wa_lead_conversao,
    }]), "KPIs");
    // Team
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.team.map(r => ({
      "Responsável": r.nome,
      "Msgs Enviadas": r.enviadas,
      "Conversas": r.conversas,
      "Tempo Médio Resposta": r.avg_response_min != null ? `${r.avg_response_min}min` : "—",
    }))), "Equipe");
    // Conversations
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.conversations.map(c => ({
      "Negócio": c.titulo,
      "Contato": c.contato_nome,
      "Responsável": c.responsavel_nome,
      "Total Msgs": c.total_msgs,
      "Enviadas": c.enviadas,
      "Recebidas": c.recebidas,
      "Não Lidas": c.nao_lidas,
      "Última Mensagem": c.ultima_msg ? new Date(c.ultima_msg).toLocaleString("pt-BR") : "",
    }))), "Conversas");
    XLSX.writeFile(wb, "relatorio_whatsapp.xlsx");
  };

  const TABS = [
    { key: "visao_geral", label: "Visão Geral" },
    { key: "equipe",      label: "Equipe" },
    { key: "conversas",   label: "Conversas" },
    { key: "distribuicao",label: "Distribuição" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <div className="animate-spin w-6 h-6 border-2 rounded-full mr-3" style={{ borderColor: `${tc500}40`, borderTopColor: tc500 }} />
        Carregando métricas de WhatsApp...
      </div>
    );
  }

  if (!data) return null;
  const { kpis, by_day, by_hour, type_dist, team, conversations } = data;

  return (
    <div className="space-y-5">
      {/* Tabs + Excel */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <button onClick={exportExcel}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors">
          <Download className="w-3.5 h-3.5" />Excel
        </button>
      </div>

      {/* ── VISÃO GERAL ── */}
      {activeTab === "visao_geral" && (
        <div className="space-y-5">
          {/* KPI grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <KpiCard icon={MessageCircle} title="Conversas" value={kpis.total_conversas} sub="no período" color="#10b981" />
            <KpiCard icon={Send} title="Msgs Enviadas" value={kpis.enviadas} sub={`${kpis.total_msgs} total`} color={tc500} />
            <KpiCard icon={Inbox} title="Msgs Recebidas" value={kpis.recebidas} sub="dos contatos" color="#3b82f6" />
            <KpiCard icon={TrendingUp} title="Taxa de Resposta" value={`${kpis.taxa_resposta}%`} sub="conversas respondidas" color="#f59e0b" />
            <KpiCard icon={Clock} title="Tempo Médio Resposta" value={kpis.avg_response_fmt} sub="primeira resposta" color="#8b5cf6" />
            <KpiCard icon={Zap} title="Leads via WhatsApp" value={kpis.wa_leads} sub="criados no período" color="#ef4444" />
            <KpiCard icon={CheckCheck} title="Conversão Leads" value={`${kpis.wa_lead_conversao}%`} sub="leads → ganhos" color="#10b981" />
            <KpiCard icon={Phone} title="Status Lido" value={`${kpis.status_dist.find(s => s.status === "read")?.count ?? 0}`} sub="msgs lidas pelo contato" color="#06b6d4" />
          </div>

          {/* Msgs por dia */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <SectionTitle>Mensagens por Dia</SectionTitle>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={by_day} barSize={10}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <RT />
                <Bar dataKey="sent" name="Enviadas" fill={tc500} radius={[2,2,0,0]} />
                <Bar dataKey="received" name="Recebidas" fill="#94a3b8" radius={[2,2,0,0]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Status distribution */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <SectionTitle>Status das Mensagens Enviadas</SectionTitle>
              {kpis.status_dist.length === 0
                ? <p className="text-xs text-slate-400 text-center py-4">Sem dados</p>
                : (
                  <div className="space-y-2">
                    {kpis.status_dist.map(s => {
                      const total = kpis.status_dist.reduce((a, b) => a + b.count, 0);
                      const pct = total ? Math.round(s.count / total * 100) : 0;
                      const color = s.status === "read" ? "#10b981" : s.status === "delivered" ? "#3b82f6" : "#94a3b8";
                      return (
                        <div key={s.status}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-600">{STATUS_LABELS[s.status] || s.status}</span>
                            <span className="font-semibold text-slate-800">{s.count} ({pct}%)</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <SectionTitle>Tipos de Mídia</SectionTitle>
              {type_dist.length === 0
                ? <p className="text-xs text-slate-400 text-center py-4">Sem dados</p>
                : (
                  <div className="space-y-2">
                    {type_dist.map((t, i) => {
                      const total = type_dist.reduce((a, b) => a + b.count, 0);
                      const pct = total ? Math.round(t.count / total * 100) : 0;
                      return (
                        <div key={t.tipo}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-600">{MEDIA_LABELS[t.tipo] || t.tipo}</span>
                            <span className="font-semibold text-slate-800">{t.count} ({pct}%)</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full">
                            <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* ── EQUIPE ── */}
      {activeTab === "equipe" && (
        <div className="space-y-4">
          {team.length === 0
            ? <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200"><Users className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">Nenhum dado de equipe no período</p></div>
            : (
              <>
                {/* Ranking */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <SectionTitle>Ranking por Msgs Enviadas</SectionTitle>
                  <ResponsiveContainer width="100%" height={Math.max(team.length * 36, 180)}>
                    <BarChart data={team} layout="vertical" barSize={16} margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={80} />
                      <RT />
                      <Bar dataKey="enviadas" name="Msgs Enviadas" fill={tc500} radius={[0,3,3,0]}>
                        {team.map((_, i) => <Cell key={i} fill={i === 0 ? tc500 : `${tc500}80`} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Responsável</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Enviadas</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Conversas</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tempo Médio 1ª Resposta</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {team.map((r, i) => (
                        <tr key={r.nome} className={i === 0 ? "bg-emerald-50/40" : ""}>
                          <td className="px-4 py-3 font-medium text-slate-800">
                            {i === 0 && <span className="text-amber-500 mr-1">🏆</span>}
                            {r.nome}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold" style={{ color: tc500 }}>{r.enviadas}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{r.conversas}</td>
                          <td className="px-4 py-3 text-right text-slate-600">
                            {r.avg_response_min != null
                              ? <span className={r.avg_response_min <= 30 ? "text-emerald-600 font-semibold" : r.avg_response_min <= 120 ? "text-amber-600" : "text-rose-500"}>
                                  {r.avg_response_min < 60 ? `${r.avg_response_min}min` : `${(r.avg_response_min/60).toFixed(1)}h`}
                                </span>
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </div>
      )}

      {/* ── CONVERSAS ── */}
      {activeTab === "conversas" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {conversations.length === 0
            ? <div className="text-center py-12 text-slate-400"><MessageCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p className="text-sm">Nenhuma conversa no período</p></div>
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negócio</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">Contato</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">Responsável</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Total</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Enviadas</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">Recebidas</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Não lidas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {conversations.map(c => (
                    <tr key={c.negocio_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-800 max-w-[180px] truncate">{c.titulo || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.contato_nome || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{c.responsavel_nome || "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{c.total_msgs}</td>
                      <td className="px-4 py-3 text-right text-slate-500 hidden sm:table-cell">{c.enviadas}</td>
                      <td className="px-4 py-3 text-right text-slate-500 hidden sm:table-cell">{c.recebidas}</td>
                      <td className="px-4 py-3 text-right">
                        {c.nao_lidas > 0
                          ? <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{c.nao_lidas}</span>
                          : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {/* ── DISTRIBUIÇÃO (Heatmap por hora) ── */}
      {activeTab === "distribuicao" && (
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <SectionTitle>Horário de Pico — Atividade por Hora do Dia</SectionTitle>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.by_hour} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 10 }} />
                <RT />
                <Bar dataKey="count" name="Mensagens" radius={[3,3,0,0]}>
                  {data.by_hour.map((entry, i) => {
                    const max = Math.max(...data.by_hour.map(h => h.count));
                    const intensity = max > 0 ? entry.count / max : 0;
                    const alpha = Math.round(30 + intensity * 170).toString(16).padStart(2, "0");
                    return <Cell key={i} fill={`${tc500}${alpha}`} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <SectionTitle>Enviadas por Hora</SectionTitle>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.by_hour_sent} barSize={12}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={3} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <RT />
                  <Bar dataKey="count" name="Enviadas" fill={tc500} radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <SectionTitle>Recebidas por Hora</SectionTitle>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.by_hour_recv} barSize={12}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={3} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <RT />
                  <Bar dataKey="count" name="Recebidas" fill="#94a3b8" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
